import { fmtUtcMonthDay } from '../../lib/format';
import { readArray, readMoney, readNumber, readRecord, readString } from '../readers';
import type { AccountUsage, UsageByApi, UsageDay } from './model';

const readApi = (value: unknown): UsageByApi => {
  const row = readRecord(value, 'API usage row');
  const api = readString(row.api, 'API usage name');
  return {
    api,
    sku: readString(row.sku, 'usage SKU'),
    requests: readNumber(row.requests, 'API request count'),
    costUsd: readMoney(row.costUsd, 'API usage USD'),
  };
};

const readDay = (value: unknown): UsageDay => {
  const row = readRecord(value, 'daily usage row');
  // Additive: the gateway and the frontend Worker deploy separately, so an
  // older gateway must not error the whole usage card.
  const date = row.date === undefined ? undefined : readString(row.date, 'usage day date');
  const label = readString(row.label, 'usage day label');
  return {
    ...(date === undefined ? {} : { date }),
    // The gateway's label is English (`Aug 28`); the same day in the reader's language.
    label: date === undefined ? label : fmtUtcMonthDay(date),
    requests: readNumber(row.requests, 'daily request count'),
    costUsd: readMoney(row.costUsd, 'daily usage USD'),
  };
};

export const readAccountUsage = (value: unknown): AccountUsage => {
  const row = readRecord(value, 'account usage');
  return {
    periodLabel: readString(row.periodLabel, 'usage period'),
    timeZone: readString(row.timeZone, 'usage time zone'),
    totalRequests: readNumber(row.totalRequests, 'total request count'),
    totalCostUsd: readMoney(row.totalCostUsd, 'total usage USD'),
    avgCostPerRequest: readMoney(row.avgCostPerRequest, 'average request cost'),
    byApi: readArray(row.byApi, 'API usage rows').map(readApi),
    daily: readArray(row.daily, 'daily usage rows').map(readDay),
  };
};
